import React, { useState } from "react";
import { CalendarDays, X } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parse = (s) => new Date(`${s}T00:00:00`);
const fmtShort = (s) => parse(s).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const shift = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export const usageDefaultRange = () => { const t = new Date(); return { start: ymd(shift(t, -6)), end: ymd(t) }; };

const PRESETS = [
    ["Today", (t) => [t, t]],
    ["Yesterday", (t) => [shift(t, -1), shift(t, -1)]],
    ["Last 7 days", (t) => [shift(t, -6), t]],
    ["This week", (t) => [shift(t, -t.getDay()), t]],
    ["Last week", (t) => [shift(t, -t.getDay() - 7), shift(t, -t.getDay() - 1)]],
    ["Last 30 days", (t) => [shift(t, -29), t]],
    ["This month", (t) => [new Date(t.getFullYear(), t.getMonth(), 1), t]],
    ["Last month", (t) => [new Date(t.getFullYear(), t.getMonth() - 1, 1), new Date(t.getFullYear(), t.getMonth(), 0)]],
    ["Year to date", (t) => [new Date(t.getFullYear(), 0, 1), t]],
    ["Lifetime", (t) => [shift(t, -179), t]],
];
const presetRange = (fn) => { const t = new Date(); t.setHours(0, 0, 0, 0); const [a, b] = fn(t); return { from: a, to: b }; };
const slug = (l) => l.toLowerCase().replace(/\s+/g, "-");

const CAL_CLASSES = {
    cell: "relative p-0 text-center text-sm [&:has([aria-selected])]:bg-[#E6F6F5] first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md [&:has(>.day-range-end)]:rounded-r-md",
    day_selected: "bg-[#2EB4AC] text-white hover:bg-[#2EB4AC] hover:text-white focus:bg-[#2EB4AC] focus:text-white",
    day_range_middle: "aria-selected:!bg-transparent aria-selected:!text-slate-900",
    day_today: "font-semibold text-[#2EB4AC]",
    head_cell: "text-slate-500 w-8 font-medium text-[0.75rem]",
};

export const UsageRangePicker = ({ range, setRange }) => {
    const [open, setOpen] = useState(false);
    const [sel, setSel] = useState({ from: parse(range.start), to: parse(range.end) });
    const [active, setActive] = useState("Last 7 days");
    const [month, setMonth] = useState(parse(range.start));
    const def = usageDefaultRange();
    const isDefault = range.start === def.start && range.end === def.end;

    const onOpen = (o) => {
        if (o) { setSel({ from: parse(range.start), to: parse(range.end) }); setMonth(parse(range.start)); }
        setOpen(o);
    };
    const apply = () => {
        if (!sel?.from) return;
        setRange({ start: ymd(sel.from), end: ymd(sel.to || sel.from) });
        setOpen(false);
    };

    return (
        <Popover open={open} onOpenChange={onOpen}>
            <PopoverTrigger asChild>
                <button className="inline-flex items-center gap-2 h-8 px-3 rounded-md border border-white/15 bg-white/5 text-xs text-white hover:bg-white/10" data-testid="usage-range-btn">
                    <CalendarDays className="h-3.5 w-3.5" />
                    {fmtShort(range.start)} – {fmtShort(range.end)}, {parse(range.end).getFullYear()}
                    {!isDefault && <X className="h-3 w-3 text-white/50 hover:text-white" onClick={(e) => { e.stopPropagation(); setRange(def); setActive("Last 7 days"); }} data-testid="usage-range-reset" />}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0 bg-white text-slate-900 border-slate-200 flex" data-testid="usage-range-popover">
                <div className="w-36 border-r border-slate-100 py-2 flex flex-col">
                    {PRESETS.map(([label, fn]) => (
                        <button
                            key={label}
                            onClick={() => { const r = presetRange(fn); setSel(r); setMonth(r.from); setActive(label); }}
                            data-testid={`usage-preset-${slug(label)}`}
                            className={`text-left text-xs px-4 py-2 transition-colors ${active === label ? "bg-slate-900 text-white font-semibold" : "text-slate-700 hover:bg-slate-100"}`}
                        >{label}</button>
                    ))}
                    <div className="mt-auto px-4 pt-2 text-[10px] text-slate-400">Asia/Kolkata</div>
                </div>
                <div className="flex flex-col">
                    <Calendar
                        mode="range"
                        showOutsideDays={false}
                        selected={sel}
                        numberOfMonths={2}
                        month={month}
                        onMonthChange={setMonth}
                        disabled={{ after: new Date() }}
                        classNames={CAL_CLASSES}
                        onSelect={(r) => { setSel(r || {}); setActive(null); }}
                    />
                    <div className="flex justify-end gap-2 px-3 pb-3">
                        <button onClick={() => setOpen(false)} data-testid="usage-range-cancel" className="h-8 px-3 rounded-md text-xs text-slate-700 hover:bg-slate-100">Cancel</button>
                        <button onClick={apply} disabled={!sel?.from} data-testid="usage-range-apply" className="h-8 px-4 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-40">Apply</button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};
